import type { i18n as I18nInstance } from 'i18next';
import { type ComponentType, lazy } from 'react';
import { getI18n, I18nextProvider } from 'react-i18next';

import hostI18n from '~/common/configs/i18n.config';

import { retryDynamicImport } from './retryDynamicImport';

const i18nByRemoteModule = new WeakMap<object, I18nInstance>();

/**
 * A remote that ships its own i18next config registers it through
 * `initReactI18next` while its module evaluates, which swaps the `react-i18next`
 * global. An unchanged global means the remote owns no instance and its keys
 * live in the host bundle, so the host instance is the correct one.
 *
 * The result is cached per module because a repeated `import()` resolves from
 * the module cache without re-evaluating, so the comparison only holds the first
 * time. `InsightsView` recreates its lazy factory on retry and depends on this.
 */
const resolveRemoteI18n = (
  remoteModule: object,
  globalBeforeImport: I18nInstance | undefined
): I18nInstance => {
  const cached = i18nByRemoteModule.get(remoteModule);
  if (cached) {
    return cached;
  }

  const globalAfterImport = getI18n();
  const resolved =
    !globalAfterImport || globalAfterImport === globalBeforeImport
      ? hostI18n
      : globalAfterImport;

  i18nByRemoteModule.set(remoteModule, resolved);

  return resolved;
};

/**
 * `React.lazy` for federated remotes, pinning each remote's subtree to the
 * i18next instance that remote owns.
 *
 * `react-i18next` is a Module Federation singleton while `i18next` is not, so
 * this app's root `I18nextProvider` reaches across the remote boundary. Without
 * this wrapper a remote would resolve its keys against host-only namespaces and
 * render raw keys whenever it is deployed independently of the host.
 */
export const lazyRemote = <
  // Remote modules are declared as untyped shorthand ambient modules, so `P`
  // has nothing to infer from and falls back to this default.
  // biome-ignore lint/suspicious/noExplicitAny: matches the remote module declarations in global.d.ts
  P extends object = any,
>(
  importRemote: () => Promise<{ default: ComponentType<P> }>
) => {
  // Reload on stale-cache chunk failures before the i18n wrapper runs, so
  // remotes get the same recovery behavior as locally lazy-loaded routes.
  const importRemoteWithReload = retryDynamicImport(importRemote);

  return lazy(async () => {
    const globalBeforeImport = getI18n();
    const remoteModule = await importRemoteWithReload();
    const RemoteComponent = remoteModule.default;
    const remoteI18n = resolveRemoteI18n(remoteModule, globalBeforeImport);

    const RemoteWithOwnI18n: ComponentType<P> = (props) => (
      <I18nextProvider i18n={remoteI18n}>
        <RemoteComponent {...props} />
      </I18nextProvider>
    );

    return { default: RemoteWithOwnI18n };
  });
};
